import { BadgeCheck, Coins, FileUp } from 'lucide-react';
import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@ui/card.tsx';

type PurchaseProofStep = {
  icon: ComponentType<{ className?: string; strokeWidth?: number; 'aria-hidden'?: boolean }>;
  title: string;
  description: string;
};

/** Explains the purchase-proof workflow through its three translated steps. */
export function PurchaseProofSteps() {
  const { t } = useTranslation();
  const steps: PurchaseProofStep[] = [
    {
      icon: FileUp,
      title: t('invoices.steps.deposit.title'),
      description: t('invoices.steps.deposit.description')
    },
    {
      icon: BadgeCheck,
      title: t('invoices.steps.review.title'),
      description: t('invoices.steps.review.description')
    },
    {
      icon: Coins,
      title: t('invoices.steps.reward.title'),
      description: t('invoices.steps.reward.description')
    }
  ];

  return (
    <section className="mb-20 flex min-h-[80vh] w-full flex-wrap py-20 lg:mb-32">
      <div className="w-full lg:w-4/12">
        <h2 className="mb-10 text-center font-bold text-2xl text-foreground md:text-3xl lg:mb-16 lg:text-start lg:text-[2.75rem] lg:leading-[3.5rem]">
          {t('invoices.steps.title')}
        </h2>
      </div>

      <div className="flex flex-col space-y-7 w-full text-foreground lg:w-8/12 lg:pl-7">
        {steps.map((step, index) => {
          const Icon = step.icon;

          return (
            <Card
              key={step.title}
              className="flex flex-col justify-start items-center w-full text-center border-0 lg:flex-row"
            >
              <div className="flex justify-center items-center p-10 pb-0 w-fit lg:p-16">
                <div className="flex justify-center items-center p-5 rounded-full bg-primary lg:p-10">
                  <Icon className="size-11 text-primary-foreground" strokeWidth={1.5} aria-hidden />
                </div>
              </div>

              <div className="flex items-center h-full">
                <div className="mr-10 hidden h-[70%] w-[2px] bg-primary lg:block" />
                <div className="flex flex-col justify-center items-center p-7 space-y-4 lg:items-start">
                  <div className="flex items-center">
                    <div className="relative p-5 mr-5 rounded-full bg-foreground text-background">
                      <span className="flex absolute top-0 left-0 justify-center items-center w-full h-full font-bold text-primary-foreground">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <h3 className="text-xl font-medium text-start">{step.title}</h3>
                  </div>
                  <p className="text-start">{step.description}</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </section>
  );
}
